// StickyToggle — Lock/Unlock pill that controls whether selected-player
// chips persist across turns within the current session, plus the small
// helper line that explains the active-locked state.
//
// Phase 3 Step 2b extraction. No new state — reads `stickyContext`,
// `loading`, and `selectedPlayers` from `useDecisionEngine()` context.
// The toggle behaviour itself (which clears chips when disabled and shows
// a toast either way) lives in the page so it can be unit-tested alongside
// the other state transitions.
import React from 'react';
import { Lock, Unlock } from 'lucide-react';

import { useDecisionEngine } from './state/DecisionEngineContext';

export const StickyToggle = ({ onToggle }) => {
  const { state } = useDecisionEngine();
  const { stickyContext, loading } = state;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={loading}
      aria-pressed={stickyContext}
      title={
        stickyContext
          ? 'Context locked — chips persist across turns in this session. Click to unlock.'
          : 'Lock chips to persist across turns in this session.'
      }
      className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border text-xs font-medium transition-colors ${
        stickyContext
          ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
          : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
      data-testid="sticky-context-toggle"
      data-state={stickyContext ? 'on' : 'off'}
    >
      {stickyContext ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
      <span>{stickyContext ? 'Context ON' : 'Context OFF'}</span>
    </button>
  );
};

// Helper strip rendered just below the InputBar row when sticky context is
// active AND there's at least one chip pinned. Pure presentational —
// derives visibility from context state.
export const StickyToggleHelper = () => {
  const { state } = useDecisionEngine();
  const { stickyContext, selectedPlayers } = state;
  if (!stickyContext || selectedPlayers.length === 0) return null;
  return (
    <div
      className="max-w-3xl mx-auto text-[11px] text-indigo-600 dark:text-indigo-400 mt-2 flex items-center gap-1"
      data-testid="sticky-context-helper"
    >
      <Lock className="h-3 w-3" />
      <span>
        Context locked to selected player{selectedPlayers.length > 1 ? 's' : ''} —
        chips will carry over across turns in this session.
      </span>
    </div>
  );
};
